import { useState, useEffect } from 'react';
import { Row, Col, Space, message, Form } from 'antd';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import { EditOutlined } from '@ant-design/icons';
import UserConfig from "@/config/user";
import ClassTimePeriodConfig from "@/config/classTimePeriod";
import SectionConfig from "@/config/section";
import { ReModal, ReButton } from "@/components/refineElement";
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import { getLangList } from '@/lib/i18n';
import { timeHrMin } from "@/lib/dateTime";
import { hasPermission } from "@/lib/permissionUtil";
import DraggableTabs from "@/components/draggableTabs";
import YearSemesterToolBar from "@/components/YearSemesterToolBar";
import { ExamType } from "@/containers/organization/semesterManagement/types";
import { TimePeriodType, ClassType, TeacherType, ClassSubjectType, SubjectSortType } from './types';
import Classes from './Classes';
import ModalInterval from './ModalInterval';
import ModalClass from './ModalClass';
import ModalSubject from './ModalSubject';

const { getAllTeacherAccount } = UserConfig;
const { getClassTimePeriods, createClassTimePeriod, updateClassTimePeriod, deleteClassTimePeriod } = ClassTimePeriodConfig;
const { getClasses, createClass, updateClass, deleteClass, updateClassSubject } = SectionConfig;

function ClassManagement() {
  const [intervalArray, setIntervalArray] = useState<TimePeriodType[]>([]);
  const [teacherArray, setTeacherArray] = useState<TeacherType[]>([]);
  const [selectedIntervalName, setSelectedIntervalName] = useState<string>('');
  const [allClassObject, setAllClassObject] = useState<{ [key: string]: ClassType[] }>({});
  const [intervalInfo, setIntervalInfo] = useState({} as TimePeriodType);
  const [classInfo, setClassInfo] = useState({} as ClassType);
  const [examList, setExamList] = useState<ExamType[]>([]);
  const [handleType, setHandleType] = useState('');
  const [isModalIntervalVisible, setIsModalIntervalVisible] = useState(false);
  const [isModalClassVisible, setIsModalClassVisible] = useState(false);
  const [isModalSubjectVisible, setIsModalSubjectVisible] = useState(false);
  const [yearSemesterForm] = Form.useForm();
  const studentListT: any = getLangList("studentList");
  const subjectT: any = getLangList("subject");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");
  const okIntervalFunction = {
    create: createTimePeriodFunction,
    update: updateTimePeriodFunction,
  };
  const okClassFunction = {
    create: createClassInfoFunction,
    update: updateClassInfoFunction,
  };

  useEffect(() => {
    getAllTeacherAccountFunction();
  }, []);

  const getTimePeriodFunction = (semesterId: number) => {
    getClassTimePeriods({ semesterId }).then((res: any) => {
      const intervalArray = res?.value ?? [];
      setIntervalArray(intervalArray);
      setSelectedIntervalName(intervalArray[0]?.timePeriodName ?? '');
    });
  };

  const getAllClassFunction = (semesterId: number) => {
    getClasses({ semesterId }).then((res: any) => {
      const allClassObject = res?.value ?? {};
      setAllClassObject(allClassObject);
    });
  };

  function getAllTeacherAccountFunction() {
    getAllTeacherAccount().then((res: any) => {
      const teacherArray = res?.value ?? [];
      setTeacherArray(teacherArray);
    });
  };

  function togglelntervalModal(handleType: string = '') {
    setIsModalIntervalVisible(!isModalIntervalVisible);
    setHandleType(handleType);
  };

  function toggleClassModal(handleType: string = '') {
    setIsModalClassVisible(!isModalClassVisible);
    setHandleType(handleType);
  };

  function toggleSubjectModal() {
    setIsModalSubjectVisible(!isModalSubjectVisible);
  };

  function clickIntervalCreate() {
    setIntervalInfo({} as TimePeriodType);
    togglelntervalModal('create');
  };

  const clickIntervalEdit = (data: TimePeriodType) => (e: any) => {
    e.stopPropagation();
    setIntervalInfo(data);
    togglelntervalModal('update');
  };

  const clickIntervalDelete = (id: number) => () => {
    showDelConfirm(() => deleteTimePeriodFunction(id));
  };

  function clickClassCreate() {
    setClassInfo({} as ClassType);
    toggleClassModal('create');
  };

  const clickClassEdit = (data: ClassType) => (e: any) => {
    e.stopPropagation();
    setClassInfo(data);
    toggleClassModal('update');
  };

  const clickClassDelete = (id: number) => () => {
    showDelConfirm(() => deleteClassInfoFunction(id));
  };

  const clickSubjectEdit = (data: ClassType, e: any) => {
    e.stopPropagation();
    setClassInfo(data);
    toggleSubjectModal();
  };

  function createTimePeriodFunction(values: TimePeriodType) {
    const { semesterId } = yearSemesterForm.getFieldsValue();
    const { timePeriodName, timeRange } = values;
    const params = {
      timePeriodName,
      startTime: timeRange[0].format(timeHrMin),
      endTime: timeRange[1].format(timeHrMin),
      semesterId,
    };

    createClassTimePeriod(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      togglelntervalModal();
      getTimePeriodFunction(semesterId);
    });
  };

  function updateTimePeriodFunction(values: TimePeriodType) {
    const { semesterId } = yearSemesterForm.getFieldsValue();
    const { id, sort } = intervalInfo;
    const { timePeriodName, timeRange } = values;
    const updateInterval = {
      timePeriodName,
      id,
      startTime: timeRange[0].format(timeHrMin),
      endTime: timeRange[1].format(timeHrMin),
      sort,
      semesterId,
    };
    const params = intervalArray.map((item: TimePeriodType) => item.id === id ? updateInterval : item);

    if (!hasPermission('ORGANIZATION_CLASS_MANAGEMENT_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateClassTimePeriod(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      togglelntervalModal();
      getTimePeriodFunction(semesterId);
      getAllClassFunction(semesterId);
    });
  }

  function updateTimePeriodSortFunction(params: TimePeriodType[]) {
    const { semesterId } = yearSemesterForm.getFieldsValue();
    updateClassTimePeriod(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getTimePeriodFunction(semesterId);
    });
  }

  function deleteTimePeriodFunction(id: number) {
    const { semesterId } = yearSemesterForm.getFieldsValue();
    const params = { id };

    deleteClassTimePeriod(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      togglelntervalModal();
      getTimePeriodFunction(semesterId);
      getAllClassFunction(semesterId);
    });
  };

  function translateIntervalClassType(selectedIntervalName: string) {
    return selectedIntervalName.replace(' ', '_').toUpperCase();
  }

  function createClassInfoFunction(values: ClassType) {
    const { semesterId } = yearSemesterForm.getFieldsValue();
    const { className, teacherId, classroom } = values;
    const params = {
      classType: translateIntervalClassType(selectedIntervalName),
      className,
      teacherUserAccountId: teacherId,
      classroom,
      semesterId
    };

    createClass(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleClassModal();
      getAllClassFunction(semesterId);
    });
  };

  function updateClassInfoFunction(values: ClassType) {
    const { classId, classSort, semesterId } = classInfo;
    const { className, teacherId, classroom } = values;
    const classArray = allClassObject?.[selectedIntervalName] ?? [];
    const updateClassItem = {
      classType: translateIntervalClassType(selectedIntervalName),
      className,
      teacherUserAccountId: teacherId,
      classroom,
      classId,
      classSort,
      semesterId,
    };
    const params = classArray.map((item: ClassType) => {
      if (item.classId === classId) {
        return updateClassItem;
      } else {
        return {
          ...item,
          classType: translateIntervalClassType(selectedIntervalName),
          teacherUserAccountId: item.teacherId,
        };
      }
    });

    updateClass(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleClassModal();
      getAllClassFunction(semesterId);
    });
  };

  function updateClassSortFunction(params: ClassType[]) {
    const { semesterId } = yearSemesterForm.getFieldsValue();
    const newParams = params.map((item: ClassType, index: number) => ({
      ...item,
      classType: translateIntervalClassType(selectedIntervalName),
      teacherUserAccountId: item.teacherId,
      classSort: index,
    }));

    updateClass(newParams).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getAllClassFunction(semesterId);
    });
  };

  function deleteClassInfoFunction(id: number) {
    const { semesterId } = yearSemesterForm.getFieldsValue();
    const params = { classId: id };

    deleteClass(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getAllClassFunction(semesterId);
    });
  };

  function updateClassSubjectFunction(values: ClassSubjectType & ClassSubjectType['subjectMap']) {
    const { semesterId } = yearSemesterForm.getFieldsValue();
    const { classId } = classInfo;
    const { template, examId, SI, SII, SIII, OP, S, C, LA } = values;

    const getSubjectMap = () => {
      const subjectMap: SubjectSortType = {};
      let sort = 0;

      if (template === 1) { // ESL
        subjectMap.SI = SI?.map((item) => ({ subjectId: item, sort: sort++ })) ?? [];
        subjectMap.SII = SII?.map((item) => ({ subjectId: item, sort: sort++ })) ?? [];
        subjectMap.SIII = SIII?.map((item) => ({ subjectId: item, sort: sort++ })) ?? [];
        subjectMap.OP = OP?.map((item) => ({ subjectId: item, sort: sort++ })) ?? [];
      }
      if (template === 2) { // GLevel
        subjectMap.S = SI?.map((item) => ({ subjectId: item, sort: sort++ })) ?? [];
        subjectMap.C = SII?.map((item) => ({ subjectId: item, sort: sort++ })) ?? [];
        subjectMap.LA = SIII?.map((item) => ({ subjectId: item, sort: sort++ })) ?? [];
      }

      return subjectMap;
    };

    const isUniq = () => {
      let selectedSubjectIdArray = [];
      let uniqSelectedSubjectIdArray = [];

      if (template === 1) { // ESL
        selectedSubjectIdArray = flatten([SI, SII, SIII, OP]);
        uniqSelectedSubjectIdArray = uniq(flatten([SI, SII, SIII, OP]));
      }
      if (template === 2) { // GLevel
        selectedSubjectIdArray = flatten([S, C, LA]);
        uniqSelectedSubjectIdArray = uniq(flatten([S, C, LA]));
      }

      return selectedSubjectIdArray.length !== uniqSelectedSubjectIdArray.length;
    };

    if (!hasPermission('ORGANIZATION_CLASS_MANAGEMENT_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    if (isUniq()) {
      return message.error(errorT.REJECT_SELECTED_THE_SAME_SUBJECT);
    }

    updateClassSubject({ classId, template, examId, subjectMap: getSubjectMap() }).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleSubjectModal();
      getAllClassFunction(semesterId);
    });
  }

  function renderIntervalTab() {
    return intervalArray.map((item: TimePeriodType) => {
      const { timePeriodName } = item;
      const classArray = allClassObject?.[timePeriodName] ?? [];
      const isShowEdit = timePeriodName === selectedIntervalName;

      return {
        key: timePeriodName,
        label: (
          <Space>
            <span>{timePeriodName}</span>
            {isShowEdit && hasPermission(['ORGANIZATION_CLASS_MANAGEMENT_EDIT', 'ORGANIZATION_CLASS_MANAGEMENT_DELETE']) &&
              <span onClick={clickIntervalEdit(item)}><EditOutlined /></span>
            }
          </Space>
        ),
        children: (
          <Classes
            classArray={classArray}
            clickClassEdit={clickClassEdit}
            clickClassDelete={clickClassDelete}
            updateClassSortFunction={updateClassSortFunction}
            clickSubjectEdit={(record: ClassType, e: any) => clickSubjectEdit(record, e)}
          />
        ),
        apiParams: item,
      };
    });
  };

  return (
    <>
      <Row justify="center">
        <Col>
          <YearSemesterToolBar
            yearSemesterForm={yearSemesterForm}
            onChangeSemester={(semesterId) => {
              getAllClassFunction(semesterId);
              getTimePeriodFunction(semesterId);
            }}
            setExamList={setExamList}
          />
        </Col>

        <Col span={24}>
          <DraggableTabs
            size="large"
            type="card"
            onChange={setSelectedIntervalName}
            activeKey={selectedIntervalName}
            destroyInactiveTabPane
            items={renderIntervalTab()}
            tabBarExtraContent={
              <Space>
                {hasPermission('ORGANIZATION_CLASS_MANAGEMENT_NEW') &&
                  <ReButton iconName='plus' onClick={clickIntervalCreate}>
                    {`${buttonT.NEW}${studentListT.INTERVAL}`}
                  </ReButton>
                }
                {hasPermission('ORGANIZATION_CLASS_MANAGEMENT_NEW') &&
                  <ReButton iconName='plus' onClick={clickClassCreate}>
                    {`${buttonT.NEW}${studentListT.CLASS}`}
                  </ReButton>
                }
              </Space>
            }
            params={{
              sortParamName: 'sort',
              updateSortFunction: updateTimePeriodSortFunction,
              hasUpdatePermission: hasPermission('ORGANIZATION_CLASS_MANAGEMENT_EDIT'),
            }}
          />
        </Col>
      </Row>

      {isModalIntervalVisible &&
        <ReModal
          title={studentListT.INTERVAL}
          onCancel={() => togglelntervalModal()}
          visible={isModalIntervalVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okIntervalFunction)}
          CollectedForm={ModalInterval}
          formProps={{
            handleType,
            info: intervalInfo,
            clickDelete: clickIntervalDelete,
            intervalArray,
          }}
        />
      }

      {isModalClassVisible &&
        <ReModal
          title={studentListT.CLASS}
          onCancel={() => toggleClassModal()}
          visible={isModalClassVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okClassFunction)}
          CollectedForm={ModalClass}
          formProps={{
            handleType,
            info: classInfo,
            teacherArray: teacherArray,
          }}
        />
      }

      {isModalSubjectVisible &&
        <ReModal
          title={subjectT.SUBJECT}
          onCancel={() => toggleSubjectModal()}
          visible={isModalSubjectVisible}
          onOk={updateClassSubjectFunction}
          CollectedForm={ModalSubject}
          width='1000px'
          formProps={{
            info: classInfo,
            examList: examList,
          }}
        />
      }
    </>
  );
}

export default ClassManagement;